/**
 * MobileEvaluationsTopMenu
 *
 * Mobile-optimized top navigation for EvaluationsView: title, export, team selector,
 * evaluation type picker and a year stepper.
 *
 * The type picker replaced a ToggleButtonGroup hardcoded to Collab / DM / Référent —
 * three buttons cannot grow with a list an admin can add to, and would not fit on a phone
 * if they did.
 */
import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { TeamSelector } from "../../../components/teams/TeamSelector";
import EvaluationTypeSelector from "../../../components/evaluations/EvaluationTypeSelector";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "12px",
  color: "rgba(0,0,0,0.7)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    transform: "scale(1.06)",
    boxShadow: "0 2px 12px rgba(255,144,21,0.18)",
    color: "rgb(255,144,21)",
  },
  "&:active": {
    transform: "scale(0.96)",
    boxShadow: "0 1px 4px rgba(255,144,21,0.12)",
  },
};

const yearChevronSx = {
  minWidth: 36,
  minHeight: 36,
  borderRadius: "10px",
  color: "rgba(0,0,0,0.6)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    color: "rgb(255,144,21)",
  },
  "&:active": {
    transform: "scale(0.93)",
  },
};

/**
 * @param {Object} props
 * @param {Array}    props.types          - [{id, title, rank}] evaluation types for the teams
 * @param {number}   props.selectedTypeId - currently selected evaluation type id
 * @param {Function} props.onTypeChange   - callback(idType)
 * @param {Date}     props.date           - current Date object (year picker)
 * @param {Function} props.onDateChange   - callback(newDate)
 * @param {Function} props.onExport       - export excel callback
 * @param {boolean}  props.showExport     - whether to show the export button
 * @param {boolean}  props.hideTypePicker - hide the picker (standalone single-type views)
 */
export default function MobileEvaluationsTopMenu({
  types,
  selectedTypeId,
  onTypeChange,
  date,
  onDateChange,
  onExport,
  showExport,
  hideTypePicker,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(20px) saturate(1.4)",
        WebkitBackdropFilter: "blur(20px) saturate(1.4)",
        borderBottom: "1px solid rgba(0,40,128,0.06)",
        boxShadow:
          "0 1px 12px rgba(0,40,128,0.05), 0 0.5px 3px rgba(0,0,0,0.03)",
        px: 1.5,
        pt: 0.75,
        pb: 1,
        transition: TRANSITION_SMOOTH,
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: "5%",
          right: "5%",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,144,21,0.15), transparent)",
        },
      }}
    >
      {/* Title + Export button row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 48,
          px: 0.5,
        }}
      >
        <Box
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            pl: 0.5,
            letterSpacing: "-0.025em",
            color: "rgb(0,0,0)",
            transition: "color 0.25s ease, opacity 0.25s ease",
            lineHeight: 1.3,
          }}
        >
          Évaluations
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {showExport && (
            <Tooltip title="Export" arrow>
              <IconButton
                onClick={onExport}
                size="large"
                sx={iconButtonSx}
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: "1.35rem" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* TeamSelector row */}
      <Box sx={{ px: 0.5 }}>
        <TeamSelector includeMyTeam />
      </Box>

      {/* Evaluation type picker */}
      {!hideTypePicker && (
        <Box sx={{ px: 0.5, pt: 0.75, pb: 0.5 }}>
          <EvaluationTypeSelector
            types={types}
            value={selectedTypeId}
            onChange={onTypeChange}
            fullWidth
          />
        </Box>
      )}

      {/* Year picker row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          pt: 0.25,
        }}
      >
        <IconButton
          size="small"
          onClick={() =>
            onDateChange(new Date(date.getFullYear() - 1, 0, 1))
          }
          sx={yearChevronSx}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            minWidth: 50,
            textAlign: "center",
            fontSize: "0.95rem",
            color: "rgba(0,0,0,0.8)",
            userSelect: "none",
          }}
        >
          {date.getFullYear()}
        </Typography>
        <IconButton
          size="small"
          onClick={() =>
            onDateChange(new Date(date.getFullYear() + 1, 0, 1))
          }
          sx={yearChevronSx}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
